// Admin Overview page — uses AdminLayout sidebar
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import { useAuth, apiCall } from "../auth/AuthContext";
import { Users, DollarSign, AlertCircle, Clock, TrendingUp, Briefcase, Layers, Banknote, FileText, Award, ArrowUpRight, ShieldCheck, Loader2 } from "lucide-react";

const fmtMoney = (n) => `$${Math.round(n || 0).toLocaleString()}`;

const LOCATION_LABELS = { hero: "Homepage — Hero", about: "Homepage — About", results: "Homepage — Results", process: "Homepage — How It Works", pricing: "Pricing Page" };

function BookingCtaBreakdown({ cta }) {
  if (!cta || cta.total === 0) return null;
  const rows = [
    ...cta.by_product.map((r) => ({ ...r, product: true })),
    ...cta.by_location.map((r) => ({ ...r, label: LOCATION_LABELS[r.label] || r.label, product: false })),
  ];
  const max = Math.max(1, ...rows.map((r) => r.clicks));
  return (
    <div data-testid="booking-cta-breakdown" className="pt-4 mt-4 border-t border-jc-gold/15">
      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold mb-3">◆ Booking Clicks by Product — {cta.total} total</p>
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.label} data-testid={`cta-row-${r.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="flex items-center gap-3">
            <span className={`w-64 flex-shrink-0 truncate font-mono text-[10px] uppercase tracking-[0.18em] ${r.product ? "text-jc-gold-light" : "text-jc-muted"}`}>{r.label}</span>
            <div className="flex-1 h-4 bg-jc-navy/40 border border-jc-gold/10 overflow-hidden">
              <div className={`h-full ${r.product ? "bg-gradient-to-r from-jc-gold to-jc-gold-light" : "bg-jc-gold/30"}`} style={{ width: `${Math.max(4, (r.clicks / max) * 100)}%` }} />
            </div>
            <span className="w-8 text-right font-mono text-[11px] text-jc-white">{r.clicks}</span>
            <span className="w-28 text-right font-mono text-[10px] text-jc-muted">
              {r.product ? `${r.booked || 0} booked · ${r.conv_pct || 0}%` : ""}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ConsentExportPanel() {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState("");

  const loadStatus = async () => {
    try { setStatus(await apiCall("/api/admin/consent-exports/status")); } catch (e) { console.error("consent export status load failed", e); }
  };
  useEffect(() => { loadStatus(); }, []);

  const runNow = async () => {
    setRunning(true); setMsg("");
    try {
      const run = await apiCall("/api/admin/consent-exports/run", { method: "POST" });
      setMsg(`Exported ${run.exported_count} consent record${run.exported_count === 1 ? "" : "s"}${run.error_count ? `, ${run.error_count} failed` : ""}.`);
      await loadStatus();
    } catch (e) {
      setMsg(`Export failed: ${e.message || "server error"}`);
    } finally { setRunning(false); }
  };

  const last = status?.last_run;
  return (
    <div data-testid="consent-export-panel" className="mt-10 border border-jc-gold/25 bg-jc-black/40 backdrop-blur-sm p-6 md:p-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold mb-2">◆ Compliance</p>
          <h2 className="font-display text-2xl text-jc-white flex items-center gap-2"><ShieldCheck size={20} className="text-jc-gold" /> CONSENT ARCHIVE</h2>
          <p className="mt-2 font-body text-sm text-jc-muted leading-relaxed max-w-lg">
            Consent trails (service + GLBA acknowledgment) are archived weekly — Sunday midnight UTC — as attachments in the Airtable DOCUMENTS table, linked to each client.
          </p>
        </div>
        <button
          onClick={runNow}
          disabled={running}
          data-testid="consent-export-now-btn"
          className="jc-btn jc-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {running ? <><Loader2 size={14} className="animate-spin" /> EXPORTING...</> : "EXPORT NOW"}
        </button>
      </div>
      <div className="mt-5 pt-4 border-t border-jc-gold/15 flex flex-wrap gap-6 font-mono text-[10px] uppercase tracking-[0.22em]">
        <span className="text-jc-muted">Pending clients: <span data-testid="consent-export-pending" className="text-jc-gold-light">{status ? status.pending_count : "—"}</span></span>
        <span className="text-jc-muted">Last run: <span data-testid="consent-export-last-run" className="text-jc-gold-light">{last ? `${new Date(last.started_at).toLocaleString()} · ${last.exported_count} exported${last.error_count ? ` · ${last.error_count} errors` : ""}` : "never"}</span></span>
      </div>
      {msg && <p data-testid="consent-export-result" className="mt-3 font-body text-sm text-jc-gold-light">{msg}</p>}
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, c, inv, f] = await Promise.all([
          apiCall("/api/homepage/stats"),
          apiCall("/api/admin/clients"),
          apiCall("/api/admin/invoices"),
          apiCall("/api/admin/funnel?period_days=30"),
        ]);
        setStats(s); setClients(c.records || []); setInvoices(inv.records || []); setFunnel(f);
      } catch (e) { console.error("AdminDashboard load failed", e); }
      finally { setLoading(false); }
    })();
  }, []);

  const collected = invoices.filter((r) => (r.Status || "").toLowerCase() === "paid").reduce((s, r) => s + (r["Net Amount"] || 0), 0);
  const pending = invoices.filter((r) => ["sent", "partial"].includes((r.Status || "").toLowerCase())).reduce((s, r) => s + (r.Amount || 0), 0);
  const overdue = invoices.filter((r) => (r.Status || "").toLowerCase() === "overdue").reduce((s, r) => s + (r.Amount || 0), 0);

  const metricCards = [
    { label: "Total Clients", value: clients.length || stats?.clients_served || "—", color: "text-jc-gold", Icon: Users },
    { label: "Revenue Collected", value: fmtMoney(collected), color: "text-emerald-300", Icon: DollarSign },
    { label: "Revenue Pending", value: fmtMoney(pending), color: "text-amber-300", Icon: Clock },
    { label: "Revenue Overdue", value: fmtMoney(overdue), color: "text-red-300", Icon: AlertCircle },
  ];

  const links = [
    { to: "/dashboard/admin/clients", title: "Client Portfolio", body: "Every active client, package, status.", Icon: Users },
    { to: "/dashboard/admin/credit", title: "Credit Pipeline", body: "Score gains across all credit advisory clients.", Icon: TrendingUp },
    { to: "/dashboard/admin/funding", title: "Funding Pipeline", body: "Personal + business funding applications.", Icon: Briefcase },
    { to: "/dashboard/admin/tradelines", title: "Tradeline Tracker", body: "Reporting timelines & countdown.", Icon: Layers },
    { to: "/dashboard/admin/grants", title: "Grant Tracker", body: "Open applications & deadlines.", Icon: Banknote },
    { to: "/dashboard/admin/documents", title: "Document Queue", body: "Pending client uploads.", Icon: FileText },
    { to: "/dashboard/admin/referrals", title: "Referral Leaderboard", body: "Top referrers & rewards.", Icon: Award },
    { to: "/dashboard/admin/revenue", title: "Revenue", body: "Invoices, collected, net of 3% processing fee.", Icon: DollarSign },
  ];

  return (
    <AdminLayout title={<><span data-testid="hello-jace">HELLO, <span className="text-jc-gold">{(user?.first_name || "JACE").toUpperCase()}.</span></span></>} kicker="Overview">
      {/* metric grid */}
      <div data-testid="admin-overview" className="grid grid-cols-2 md:grid-cols-4 gap-px bg-jc-gold/15 mb-10">
        {metricCards.map((m) => (
          <div key={m.label} data-testid={`metric-${m.label.toLowerCase().replace(/\s+/g, "-")}`} className="bg-jc-black p-5">
            <div className="flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-jc-gold">{m.label}</div>
              <m.Icon size={14} className={m.color} />
            </div>
            <div className={`mt-3 font-display text-3xl md:text-4xl ${m.color === "text-jc-gold" ? "text-jc-gold" : "text-jc-white"}`}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* quick action cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {links.map((c, i) => (
          <Link key={c.to} to={c.to} data-testid={`admin-card-${i + 1}`} className="group border border-jc-gold/25 bg-jc-black/40 backdrop-blur-sm p-5 hover:border-jc-gold/70 hover:bg-jc-navy/30 transition-colors">
            <div className="flex items-center justify-between">
              <div className="h-9 w-9 border border-jc-gold/40 flex items-center justify-center"><c.Icon size={16} className="text-jc-gold" /></div>
              <ArrowUpRight size={14} className="text-jc-gold/50 group-hover:text-jc-gold transition-colors" />
            </div>
            <h3 className="mt-4 font-display text-lg text-jc-white tracking-wide">{c.title.toUpperCase()}</h3>
            <p className="mt-2 font-body text-sm text-jc-muted leading-relaxed">{c.body}</p>
          </Link>
        ))}
      </div>

      <div className="mt-10 border-l-2 border-jc-gold px-6 py-5 bg-jc-navy/30">
        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-jc-gold-light mb-1">◆ Data Source</p>
        <p className="font-body text-base text-jc-muted leading-relaxed">
          Every table pulls live from Airtable base <span className="font-mono text-jc-gold-light">appQcMNMEpgW2UkHN</span>. Edit records in Airtable and refresh here to see updates instantly.
        </p>
      </div>

      <ConsentExportPanel />

      {/* Funnel — last 30 days */}
      <div data-testid="admin-funnel" className="mt-10 border border-jc-gold/25 bg-jc-black/40 backdrop-blur-sm p-6 md:p-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold mb-2">◆ Funnel — Last 30 Days</p>
            <h2 className="font-display text-2xl md:text-3xl text-jc-white">CONVERSION JOURNEY</h2>
          </div>
          {funnel?.status === "table_missing" && (
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300">⚠ Create EVENTS table in Airtable to populate</span>
          )}
        </div>
        {(() => {
          const m = funnel?.metrics || {
            page_views: 0, lead_submits: 0, signups: 0, bookings_completed: 0, payments_completed: 0,
            rate_view_to_lead: 0, rate_lead_to_signup: 0, rate_signup_to_booking: 0, rate_booking_to_payment: 0,
          };
          const steps = [
            { key: "page_views", label: "Page Views", value: m.page_views, dropTo: null },
            { key: "lead_submits", label: "Leads", value: m.lead_submits, dropTo: m.rate_view_to_lead },
            { key: "signups", label: "Signups", value: m.signups, dropTo: m.rate_lead_to_signup },
            { key: "bookings_completed", label: "Calls Booked", value: m.bookings_completed, dropTo: m.rate_signup_to_booking },
            { key: "payments_completed", label: "Paid Clients", value: m.payments_completed, dropTo: m.rate_booking_to_payment },
          ];
          const max = Math.max(1, ...steps.map((s) => s.value));
          return (
            <div className="space-y-2">
              {steps.map((s, i) => {
                const widthPct = Math.max(8, (s.value / max) * 100);
                return (
                  <div key={s.key} data-testid={`funnel-step-${s.key}`}>
                    <div className="flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.22em] mb-1">
                      <span className="text-jc-gold">{`0${i + 1}`.slice(-2)} · {s.label}</span>
                      <span className="text-jc-muted">
                        {i > 0 && s.dropTo != null && <span className="text-jc-gold-light mr-3">{s.dropTo}%</span>}
                        <span className="text-jc-white font-semibold">{s.value.toLocaleString()}</span>
                      </span>
                    </div>
                    <div className="h-7 bg-jc-navy/40 border border-jc-gold/15 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-jc-gold to-jc-gold-light transition-all duration-700" style={{ width: `${widthPct}%` }} />
                    </div>
                  </div>
                );
              })}
              <div className="pt-3 mt-4 border-t border-jc-gold/15 flex flex-wrap gap-6 font-mono text-[10px] uppercase tracking-[0.22em]">
                <span className="text-jc-muted">View → Signup: <span className="text-jc-gold-light">{m.rate_view_to_signup}%</span></span>
                <span className="text-jc-muted">View → Payment: <span className="text-jc-gold-light">{m.rate_view_to_payment}%</span></span>
              </div>
              <BookingCtaBreakdown cta={funnel?.booking_cta} />
            </div>
          );
        })()}
      </div>
      {loading && <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-jc-muted">◆ Loading live data...</p>}
    </AdminLayout>
  );
}
